import React, { useState, useEffect, useContext } from 'react';
import { ToastContainer } from 'react-toastify';
import axiosInstance from '../../utils/axios';
import AuthContext from '../../context/AuthContext';
import BusyLoaderOverlay from '../reusables/BusyLoaderOverlay';
import { showToast } from '../reusables/CommonFunction';
import { Row, Col, Card, Button, Form } from 'react-bootstrap';
import { BarChart3, Users, UserPlus, TrendingUp, Calendar, IndianRupee } from 'lucide-react';
import { Bar, Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

function DateRangePicker({ startDate, endDate, onStartDateChange, onEndDateChange, onPresetSelect, onPressCustomRange, activePreset }) {
    return (
        <div className="d-flex flex-wrap align-items-center gap-3">
            <div className="d-flex gap-2">
                <Button variant={activePreset === 'today' ? 'secondary' : 'outline-secondary'} onClick={() => onPresetSelect('today')}>Today</Button>
                <Button variant={activePreset === 'week' ? 'secondary' : 'outline-secondary'} onClick={() => onPresetSelect('week')}>This Week</Button>
                <Button variant={activePreset === 'month' ? 'secondary' : 'outline-secondary'} onClick={() => onPresetSelect('month')}>This Month</Button>
                <Button variant={activePreset === 'custom' ? 'secondary' : 'outline-secondary'} onClick={() => onPressCustomRange()}>Custom Range</Button>
            </div>
            <div className="d-flex align-items-center gap-2">
                <Form.Control
                    type="date"
                    value={startDate.toISOString().split('T')[0]}
                    onChange={(e) => {
                        onStartDateChange(new Date(e.target.value))
                    }}
                />
                <span>to</span>
                <Form.Control
                    type="date"
                    value={endDate.toISOString().split('T')[0]}
                    onChange={(e) => {
                        onEndDateChange(new Date(e.target.value))
                    }}
                />
            </div>
        </div>
    );
}

const PatientVisitAnalytics = () => {
    let { userDetails, clinicBranchDetails } = useContext(AuthContext)
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [clinicBranchId, setClinicBranchId] = useState(null);
    const [initialStartDate, setInitialStartDate] = useState(new Date());
    const [initialEndDate, setInitialEndDate] = useState(new Date());
    const isDentist = userDetails?.doctor_type?.toLowerCase() === "dentist";

    const [activePreset, setActivePreset] = useState('month');
    const [patientAnalyticsData, setPatientAnalyticsData] = useState({
        dailyData: [],
        totals: { new: 0, existing: 0 },
        previousPeriod: { new: 0, existing: 0 },
        revenueSummary: {
            current: { new_patient_amount: 0, existing_patient_amount: 0, dental_treatment_amount: 0 },
            previous: { new_patient_amount: 0, existing_patient_amount: 0, previous_dental_treatment_amount: 0 }
        }
    });

    const fetchPatientAnalyticsData = (start, end) => {
        setLoading(true);
        axiosInstance
            .get(`/patients/patient_analytics/`, {
                params: {
                    clinicId: userDetails.clinic_id,
                    clinicBranchId: clinicBranchId || null,
                    start_date: start.toISOString().split('T')[0],
                    end_date: end.toISOString().split('T')[0]
                }
            })
            .then(response => {
                if (response.status === 200) {
                    const data = response.data;
                    setPatientAnalyticsData({
                        dailyData: (data.dailyData || []).map(item => ({
                            ...item,
                            date: new Date(item.date),
                            new: Number(item.new) || 0,
                            existing: Number(item.existing) || 0
                        })),
                        totals: {
                            new: Number(data.totals?.new) || 0,
                            existing: Number(data.totals?.existing) || 0
                        },
                        previousPeriod: {
                            new: Number(data.previousPeriod?.new) || 0,
                            existing: Number(data.previousPeriod?.existing) || 0
                        },
                        revenueSummary: {
                            current: {
                                new_patient_amount: Number(data.revenue_summary?.current?.new_patient_amount || 0),
                                existing_patient_amount: Number(data.revenue_summary?.current?.existing_patient_amount || 0),
                                ...(userDetails?.doctor_type?.toLowerCase() === "dentist" && {
                                    dental_treatment_amount: Number(data.revenue_summary?.current?.dental_treatment_amount || 0)
                                })
                            },
                            previous: {
                                new_patient_amount: Number(data.revenue_summary?.previous?.new_patient_amount || 0),
                                existing_patient_amount: Number(data.revenue_summary?.previous?.existing_patient_amount || 0),
                                ...(userDetails?.doctor_type?.toLowerCase() === "dentist" && {
                                    previous_dental_treatment_amount: Number(data.revenue_summary?.previous?.previous_dental_treatment_amount || 0)
                                })
                            }
                        }
                    });
                }
            })
            .catch(error => {
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const onPressCustomRange = () => {
        if (!startDate || !endDate) {
            showToast("error", "Start date and End date cannot be empty!");
            return;
        }
        if (startDate > endDate) {
            showToast("error", "Start date cannot be greater than End date!");
            return;
        }
        if (endDate < startDate) {
            showToast("error", "End date cannot be less than Start date!");
            return;
        }
        if (startDate === initialStartDate && endDate === initialEndDate) {
            showToast("info", "Please modify Start Date or End Date before submitting.");
            setActivePreset(activePreset);
            return;
        }

        setActivePreset('custom');
        fetchPatientAnalyticsData(startDate, endDate);
    }

    const handlePresetSelect = (preset) => {
        const today = new Date();
        let start = new Date();
        let end = new Date();

        switch (preset) {
            case 'today':
                start = new Date(today);
                start.setDate(today.getDate());

                end = new Date(today);
                end.setDate(today.getDate());
                break;
            case 'week':
                start = new Date(today);
                start.setDate(today.getDate() - 6);

                end = new Date(today);
                end.setDate(today.getDate());
                break;
            case 'month':
                start = new Date(today.getFullYear(), today.getMonth(), 2);
                end = new Date(today.getFullYear(), today.getMonth() + 1, 1);
                break;
            default:
                return;
        }

        setStartDate(start);
        setEndDate(end);
        setInitialStartDate(start);
        setInitialEndDate(end);
        setActivePreset(preset);
        fetchPatientAnalyticsData(start, end);
    };

    const calculatePercentageChange = (current, previous) => {
        const currentVal = Number(current) || 0;
        const previousVal = Number(previous) || 0;

        if (previous === 0) {
            return 'Significantly'
        } else {
            const percentageChange = ((currentVal - previousVal) / previousVal * 100).toFixed(1);
            return `${percentageChange}%`;
        }
    };

    const newPatientChange = calculatePercentageChange(
        patientAnalyticsData.totals.new,
        patientAnalyticsData.previousPeriod.new
    );

    const totalPatientChange = calculatePercentageChange(
        patientAnalyticsData.totals.new + patientAnalyticsData.totals.existing,
        patientAnalyticsData.previousPeriod.new + patientAnalyticsData.previousPeriod.existing
    );

    const newRevenueChange = calculatePercentageChange(
        patientAnalyticsData.revenueSummary.current.new_patient_amount,
        patientAnalyticsData.revenueSummary.previous.new_patient_amount
    );

    const existingRevenueChange = calculatePercentageChange(
        patientAnalyticsData.revenueSummary.current.existing_patient_amount,
        patientAnalyticsData.revenueSummary.previous.existing_patient_amount
    );

    const barChartData = {
        labels: patientAnalyticsData.dailyData.map(d => d.date.toLocaleDateString()),
        datasets: [
            {
                label: 'New Patients',
                data: patientAnalyticsData.dailyData.map(d => d.new),
                backgroundColor: 'rgba(40, 167, 69, 0.6)',
                borderColor: 'rgb(40, 167, 69)',
                borderWidth: 1
            },
            {
                label: 'Existing Patients',
                data: patientAnalyticsData.dailyData.map(d => d.existing),
                backgroundColor: 'rgba(111, 66, 193, 0.6)',
                borderColor: 'rgb(111, 66, 193)',
                borderWidth: 1
            }
        ]
    };

    const pieChartData = {
        labels: ['New Patients', 'Existing Patients'],
        datasets: [{
            data: [
                Number(patientAnalyticsData.totals.new) || 0,
                Number(patientAnalyticsData.totals.existing) || 0
            ],
            backgroundColor: [
                'rgba(40, 167, 69, 0.6)',
                'rgba(111, 66, 193, 0.6)'
            ],
            borderColor: [
                'rgb(40, 167, 69)',
                'rgb(111, 66, 193)'
            ],
            borderWidth: 1
        }]
    };

    useEffect(() => {
        // Set initial date range to current month
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth(), 2);
        const end = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        setStartDate(start);
        setEndDate(end);
        setInitialStartDate(start);
        setInitialEndDate(end);
    }, []);

    useEffect(() => {
        if (startDate && endDate && userDetails?.clinic_id) {
            fetchPatientAnalyticsData(startDate, endDate);
        }
    }, [startDate, endDate, clinicBranchId, userDetails]);

    return (
        <>
            <Row className='ml-3 mr-3 mb-3'>
                <div className='col-md-12'>
                    <Card className="mb-4">
                        <Card.Body>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <Calendar size={20} className="text-muted" />
                                <h5 className="mb-0">Select Date Range</h5>
                            </div>
                            <Row className='mb-3'>
                                <Col xs={12}>
                                    <DateRangePicker
                                        startDate={startDate}
                                        endDate={endDate}
                                        onStartDateChange={setStartDate}
                                        onEndDateChange={setEndDate}
                                        onPresetSelect={handlePresetSelect}
                                        onPressCustomRange={onPressCustomRange}
                                        activePreset={activePreset}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={12} className="mb-3">
                                    <Form.Group>
                                        <Form.Label className="fw-bold">Clinic Branch</Form.Label>
                                        <Form.Select
                                            value={clinicBranchId || ""}
                                            onChange={(oEvent) => setClinicBranchId(oEvent.target.value)}>
                                            <option value="">All Clinic Branch</option>
                                            {clinicBranchDetails.map((branch) => (
                                                <option key={branch.clinic_branch_id} value={branch.clinic_branch_id}>
                                                    {branch.branch_address}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <Row className="mb-4">
                        <Col md={4}>
                            <Card>
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <p className="text-muted mb-1">Total Visits</p>
                                            <h3>{(Number(patientAnalyticsData.totals.new) || 0) + (Number(patientAnalyticsData.totals.existing) || 0)}</h3>
                                        </div>
                                        <div className="bg-primary bg-opacity-10 p-3 rounded">
                                            <Users className="text-primary" />
                                        </div>
                                    </div>
                                    <div className="mt-3 d-flex align-items-center text-success">
                                        <TrendingUp size={16} className="me-1" />
                                        <span>{totalPatientChange}% vs previous period</span>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card>
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <p className="text-muted mb-1">New Patients</p>
                                            <h3>{Number(patientAnalyticsData.totals.new) || 0}</h3>
                                        </div>
                                        <div className="bg-success bg-opacity-10 p-3 rounded">
                                            <UserPlus className="text-success" />
                                        </div>
                                    </div>
                                    <div className="mt-3 d-flex align-items-center text-success">
                                        <TrendingUp size={16} className="me-1" />
                                        <span>{newPatientChange} vs previous period</span>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card>
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <p className="text-muted mb-1">Existing Patients</p>
                                            <h3>{Number(patientAnalyticsData.totals.existing) || 0}</h3>
                                        </div>
                                        <div className="bg-purple bg-opacity-10 p-3 rounded">
                                            <BarChart3 className="text-purple" />
                                        </div>
                                    </div>
                                    <div className="mt-3 d-flex align-items-center text-success">
                                        <TrendingUp size={16} className="me-1" />
                                        <span>
                                            {calculatePercentageChange(patientAnalyticsData.totals.existing, patientAnalyticsData.previousPeriod.existing)} vs previous period
                                        </span>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Row className="mb-4">
                        <Col lg={6}>
                            <Card>
                                <Card.Body>
                                    <h5>Daily Patient Visits</h5>
                                    <Bar data={barChartData} options={{
                                        responsive: true,
                                        scales: {
                                            x: { stacked: true },
                                            y: { stacked: true, ticks: { precision: 0 } }
                                        }
                                    }} />
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col lg={6}>
                            <Card>
                                <Card.Body>
                                    <h5>Patient Distribution</h5>
                                    <div style={{ height: '300px' }} className="d-flex justify-content-center">
                                        <Pie data={pieChartData} options={{
                                            responsive: true,
                                            maintainAspectRatio: false
                                        }} />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Row className="mb-4">
                        <Col md={isDentist ? 3 : 4}>
                            <Card>
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <p className="text-muted mb-1">Total Revenue</p>
                                            <h4>
                                                ₹{(
                                                    isDentist
                                                        ? (
                                                            (patientAnalyticsData.revenueSummary.current.new_patient_amount || 0) +
                                                            (patientAnalyticsData.revenueSummary.current.existing_patient_amount || 0) +
                                                            (patientAnalyticsData.revenueSummary.current.dental_treatment_amount || 0)
                                                        )
                                                        : (
                                                            (patientAnalyticsData.revenueSummary.current.new_patient_amount || 0) +
                                                            (patientAnalyticsData.revenueSummary.current.existing_patient_amount || 0)
                                                        )
                                                ).toFixed(2)}
                                            </h4>
                                        </div>
                                        <div className="bg-success bg-opacity-10 p-3 rounded">
                                            <IndianRupee className="text-dark" />
                                        </div>
                                    </div>
                                    <div className="mt-2 text-success d-flex align-items-center">
                                        <TrendingUp size={16} className="me-1" />
                                        <span>
                                            {isDentist
                                                ? `${calculatePercentageChange(
                                                    patientAnalyticsData.revenueSummary.current.new_patient_amount +
                                                    patientAnalyticsData.revenueSummary.current.existing_patient_amount +
                                                    (patientAnalyticsData.revenueSummary.current.dental_treatment_amount || 0),
                                                    patientAnalyticsData.revenueSummary.previous.new_patient_amount +
                                                    patientAnalyticsData.revenueSummary.previous.existing_patient_amount +
                                                    (patientAnalyticsData.revenueSummary.previous.previous_dental_treatment_amount || 0)
                                                )} vs previous period`
                                                : `${calculatePercentageChange(
                                                    patientAnalyticsData.revenueSummary.current.new_patient_amount +
                                                    patientAnalyticsData.revenueSummary.current.existing_patient_amount,
                                                    patientAnalyticsData.revenueSummary.previous.new_patient_amount +
                                                    patientAnalyticsData.revenueSummary.previous.existing_patient_amount
                                                )} vs previous period`}
                                        </span>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={isDentist ? 3 : 4}>
                            <Card>
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <p className="text-muted mb-1">New Patient Revenue</p>
                                            <h4>₹{patientAnalyticsData.revenueSummary.current.new_patient_amount.toFixed(2)}</h4>
                                        </div>
                                        <div className="bg-success bg-opacity-10 p-3 rounded">
                                            <IndianRupee className="text-success" />
                                        </div>
                                    </div>
                                    <div className="mt-2 text-success d-flex align-items-center">
                                        <TrendingUp size={16} className="me-1" />
                                        <span>{newRevenueChange} vs previous period</span>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={isDentist ? 3 : 4}>
                            <Card>
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <p className="text-muted mb-1">Existing Patient Revenue</p>
                                            <h4>₹{patientAnalyticsData.revenueSummary.current.existing_patient_amount.toFixed(2)}</h4>
                                        </div>
                                        <div className="bg-primary bg-opacity-10 p-3 rounded">
                                            <IndianRupee className="text-primary" />
                                        </div>
                                    </div>
                                    <div className="mt-2 text-success d-flex align-items-center">
                                        <TrendingUp size={16} className="me-1" />
                                        <span>{existingRevenueChange} vs previous period</span>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {isDentist && (
                            <Col md={isDentist ? 3 : 4}>
                                <Card>
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <p className="text-muted mb-1">Dental Treatment Revenue</p>
                                                <h4>₹{patientAnalyticsData.revenueSummary.current.dental_treatment_amount.toFixed(2)}</h4>
                                            </div>
                                            <div className="bg-info bg-opacity-10 p-3 rounded">
                                                <IndianRupee className="text-info" />
                                            </div>
                                        </div>
                                        <div className="mt-2 text-success d-flex align-items-center">
                                            <TrendingUp size={16} className="me-1" />
                                            <span>{calculatePercentageChange(
                                                patientAnalyticsData.revenueSummary.current.dental_treatment_amount,
                                                patientAnalyticsData.revenueSummary.previous.previous_dental_treatment_amount
                                            )} vs previous period</span>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                    </Row>

                    <Card bg="success" text="white" className="bg-opacity-10 border-success">
                        <Card.Body>
                            <h5 className="text-success">Period Highlights</h5>
                            <ul className="list-unstyled mb-0">
                                <li className="d-flex align-items-center mb-2">
                                    <TrendingUp size={20} className="me-2 text-success" />
                                    <span className="text-success">New patients increased by {newPatientChange.toLowerCase()} compared to previous period!</span>
                                </li>
                                <li className="d-flex align-items-center mb-2">
                                    <TrendingUp size={20} className="me-2 text-success" />
                                    <span className="text-success">Overall patient visits grew by {totalPatientChange.toLowerCase()} this period.</span>
                                </li>
                                <li className="d-flex align-items-center mb-2">
                                    <TrendingUp size={20} className="me-2 text-success" />
                                    <span className="text-success">New patient revenue changed by {newRevenueChange.toLowerCase()} compared to previous period.</span>
                                </li>
                                <li className="d-flex align-items-center mb-2">
                                    <TrendingUp size={20} className="me-2 text-success" />
                                    <span className="text-success">
                                        Existing patient revenue changed by {existingRevenueChange.toLowerCase()} from previous period.
                                    </span>
                                </li>
                                <li className="d-flex align-items-center mb-2">
                                    <TrendingUp size={20} className="me-2 text-success" />
                                    <span className="text-success">
                                        {isDentist
                                            ? `Total revenue including dental treatments grew by ${calculatePercentageChange(
                                                patientAnalyticsData.revenueSummary.current.new_patient_amount +
                                                patientAnalyticsData.revenueSummary.current.existing_patient_amount +
                                                (patientAnalyticsData.revenueSummary.current.dental_treatment_amount || 0),
                                                patientAnalyticsData.revenueSummary.previous.new_patient_amount +
                                                patientAnalyticsData.revenueSummary.previous.existing_patient_amount +
                                                (patientAnalyticsData.revenueSummary.previous.previous_dental_treatment_amount || 0)
                                            ).toLowerCase()} compared to previous period.`
                                            : `Total revenue grew by ${calculatePercentageChange(
                                                patientAnalyticsData.revenueSummary.current.new_patient_amount +
                                                patientAnalyticsData.revenueSummary.current.existing_patient_amount,
                                                patientAnalyticsData.revenueSummary.previous.new_patient_amount +
                                                patientAnalyticsData.revenueSummary.previous.existing_patient_amount
                                            ).toLowerCase()} compared to previous period.`}
                                    </span>
                                </li>
                            </ul>
                        </Card.Body>
                    </Card>
                </div>
            </Row>
            <ToastContainer />
            <BusyLoaderOverlay show={loading} />
        </>
    );
}

export default PatientVisitAnalytics;